import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import { LlmForm, type LlmFormValues } from "@/components/llm-form";

export const Route = createFileRoute("/_authed/llms/new")({
  component: NewLlmPage,
});

function NewLlmPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (values: LlmFormValues) => {
      const { data, error: apiError } = await api.POST("/api/v1/admin/llms", {
        body: {
          code: values.code,
          name: values.name,
          provider: values.provider,
          provider_model: values.provider_model,
          api_key: values.api_key,
          notes: values.notes || null,
          is_active: values.is_active,
        },
      });
      if (apiError) {
        const detail =
          (apiError as { detail?: string })?.detail ?? "create failed";
        throw new Error(String(detail));
      }
      return data!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "llms"] });
      navigate({ to: "/llms" });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "create failed"),
  });

  return (
    <div className="space-y-4">
      <Link
        to="/llms"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ChevronLeft className="size-4" />
        All LLMs
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>New LLM</CardTitle>
        </CardHeader>
        <CardBody>
          <LlmForm
            mode="create"
            submitting={create.isPending}
            error={error}
            onSubmit={(v) => {
              setError(null);
              create.mutate(v);
            }}
            onCancel={() => navigate({ to: "/llms" })}
          />
        </CardBody>
      </Card>
    </div>
  );
}
